/**
 * Seller-facing error copy.
 *
 * Sellers must never see a raw exception, a stack, an HTTP status or a
 * provider message. Every failure is translated here into one calm sentence
 * that says what happened and what to do next; the original text is kept for
 * the admin diagnostics panel only.
 */

import { imgError, type ImagingScope } from "./diagnostics";
import { errorMessage } from "./reliability";

export type FriendlyFailure = {
  /** Shown to the seller. */
  message: string;
  /** Shown only in the admin diagnostics panel. */
  technical: string;
};

const RULES: { test: RegExp; message: string }[] = [
  {
    test: /timed out|timeout|aborted|deadline/i,
    message: "That step took longer than expected. Tap retry — your photo is safe.",
  },
  {
    test: /network|fetch|failed to fetch|connection|offline|ECONN|ENOTFOUND/i,
    message: "The connection dropped. Check your signal and retry — nothing was lost.",
  },
  {
    test: /429|rate.?limit|too many requests/i,
    message: "The scanner is busy right now. Wait a few seconds and retry.",
  },
  {
    test: /402|credit|quota|billing/i,
    message: "AI scanning is temporarily unavailable. You can still fill the details in manually.",
  },
  {
    test: /401|403|unauthor|forbidden|jwt|session/i,
    message: "Your session expired. Sign in again and we will pick up where you left off.",
  },
  {
    test: /413|too (big|large)|size|exceeds/i,
    message: "That photo is too large. Try a slightly smaller image or retake it.",
  },
  {
    test: /decode|heic|unsupported|corrupt|not an image/i,
    message: "We could not read that photo. Retake it, or upload a JPEG or PNG instead.",
  },
  {
    test: /storage|bucket|upload/i,
    message: "The upload did not complete. Tap retry to send it again.",
  },
  {
    test: /detect|quad|confidence/i,
    message: "We could not find the card edges automatically. Use Adjust crop to set them.",
  },
];

/**
 * Translate any thrown value into seller-safe copy, logging the technical
 * detail to the diagnostics bus at the same time.
 */
export function friendly(err: unknown, scope: ImagingScope, context: string): FriendlyFailure {
  const technical = errorMessage(err, "Unknown error");
  imgError(scope, `${context}: ${technical}`, err);
  const rule = RULES.find((r) => r.test.test(technical));
  return {
    message: rule?.message ?? "Something went wrong on that step. Tap retry to try again.",
    technical,
  };
}
